import {motion} from 'framer-motion';
import {IconButton, useTranslationContext} from 'platform/components';
import {Heading, Show} from 'platform/foundation';

import {createPortal} from 'react-dom';

import {isTruthy} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

type MobileSheetProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  badgeCount?: number;
  footer?: React.ReactNode;
  children: React.ReactNode;
} & TestIdProps;

const SHEET_ANIMATION = {
  initial: {y: '100%'},
  animate: {y: 0},
  transition: {duration: 0.26, ease: [0.32, 0.72, 0, 1] as const},
};

const FOOTER_BORDER = {borderTop: '1px solid #EBECF0'} as const;

/**
 * Mobile bottom sheet: scrim + slide-up panel portalled to document.body.
 * Entry-only animation (no AnimatePresence exit) — matches the instant-hide
 * precedent of MobileFilterPanel / the compact sidebar drawer.
 */
export function MobileSheet(props: MobileSheetProps) {
  const t = useTranslationContext();

  if (!props.isOpen) {
    return null;
  }

  return createPortal(
    <>
      <div
        // z-fullscreen-modal-component keeps the sheet above dialogs (z-modal)
        // and FullScreenModals (z-fullscreen-modal) when the grid is inside one.
        className="fixed inset-0 bg-black/30 z-(--z-fullscreen-modal-component)"
        // stopPropagation: portalled or not, the click still bubbles through the REACT tree —
        // e.g. into the sortable <th> that mounts the column-menu sheet, toggling its sort.
        onClick={(event) => {
          event.stopPropagation();
          props.onClose();
        }}
        data-testid={suffixTestId('mobileSheetScrim', props)}
      />
      <motion.div
        className="fixed inset-x-0 bottom-0 max-h-[85vh] bg-white rounded-t-[18px] flex flex-col overflow-hidden z-(--z-fullscreen-modal-component)"
        initial={SHEET_ANIMATION.initial}
        animate={SHEET_ANIMATION.animate}
        transition={SHEET_ANIMATION.transition}
        onClick={(e) => e.stopPropagation()}
        data-testid={suffixTestId('mobileSheet', props)}
      >
        {/* grab handle */}
        <div className="w-[38px] h-[5px] rounded-full bg-black/20 mx-auto mt-2 mb-1 flex-shrink-0" />
        <div className="flex items-center justify-between px-4 py-2 flex-shrink-0">
          <span className="flex items-center gap-2 min-w-0">
            <Heading size={4}>{props.title}</Heading>
            <Show when={(props.badgeCount ?? 0) > 0}>
              <span
                className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#0065FF] text-white text-[10px] font-bold"
                data-testid={suffixTestId('mobileSheetBadge', props)}
              >
                {props.badgeCount}
              </span>
            </Show>
          </span>
          <IconButton
            size="small"
            icon="navigation/close"
            aria-label={t('page.datagrid.button.close')}
            onClick={props.onClose}
          />
        </div>
        <div className="flex-1 overflow-y-auto">{props.children}</div>
        <Show when={isTruthy(props.footer)}>
          <div className="flex gap-2 px-4 py-3 flex-shrink-0" style={FOOTER_BORDER}>
            {props.footer}
          </div>
        </Show>
      </motion.div>
    </>,
    document.body
  );
}
